import { Component } from '@theme/component';

/**
 * @typedef {object} KatuaStickyAtcRefs
 * @property {HTMLElement} bar - The fixed bar (a manual popover when supported).
 * @property {HTMLButtonElement} addButton - The add to cart button.
 * @property {HTMLElement} addLabel - The add to cart button text.
 * @property {HTMLElement} addPrice - The price shown next to the button text.
 * @property {HTMLButtonElement} [sizeTrigger] - Opens the size panel, when the product has a size option.
 * @property {HTMLElement} [sizeValue] - The chosen size shown in the trigger.
 * @property {HTMLElement} [sizePanel] - The panel with one button per size.
 * @property {HTMLButtonElement[]} [sizeOptions] - The size buttons.
 * @property {HTMLElement} [quantityValue] - The quantity display, when enabled.
 */

/**
 * Katua sticky add-to-cart bar. Shown once the main buy buttons scroll above the viewport.
 * The size starts unselected and add to cart stays disabled until one is picked. The size is
 * mirrored with the main variant picker, and adding goes through the main product form so the
 * cart drawer and cart events behave exactly like the main button.
 *
 * @extends Component<KatuaStickyAtcRefs>
 */
class KatuaStickyAtc extends Component {
  requiredRefs = ['bar', 'addButton', 'addLabel', 'addPrice'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #quantity = 1;
  #selectedSize = '';
  #buyButtonsVisible = true;
  #footerVisible = false;
  #desktopQuery = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#section?.addEventListener('change', this.#handleMainChange, { signal });
    this.#desktopQuery.addEventListener('change', () => this.#updateVisibility(), { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointer, { signal });

    this.#observe();
    this.#syncFromMain();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  toggleSizePanel() {
    if (this.#isPanelOpen) {
      this.#closeSizePanel();
    } else {
      this.#openSizePanel();
    }
  }

  /**
   * Picks a size from the panel and mirrors it onto the main variant picker.
   * @param {MouseEvent} event
   */
  selectSize(event) {
    const option = event.target;
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    this.#selectedSize = option.value;
    this.#render();

    const radio = this.#findMainSizeRadio(option.value);
    if (radio && !radio.checked) radio.click();

    this.#closeSizePanel({ restoreFocus: true });
  }

  decreaseQuantity() {
    this.#setQuantity(this.#quantity - 1);
  }

  increaseQuantity() {
    this.#setQuantity(this.#quantity + 1);
  }

  /**
   * Adds through the main product form. Without a size, opens the size panel instead.
   */
  handleAdd() {
    const { addButton } = this.refs;

    if (addButton.getAttribute('aria-disabled') === 'true') {
      this.#promptSizeSelection();
      return;
    }

    const form = this.#productForm;
    const mainButton = form?.querySelector('[ref="addToCartButton"]');
    if (!form || !(mainButton instanceof HTMLButtonElement)) return;

    // Make sure the main picker holds the sticky size before submitting.
    if (this.#selectedSize) {
      const radio = this.#findMainSizeRadio(this.#selectedSize);
      if (radio && !radio.checked) radio.click();
    }

    const formElement = form.querySelector('form');
    if (mainButton.disabled || !formElement) return;

    // The main form may have no quantity field (the quantity block can be disabled), so submit the
    // sticky quantity through a temporary field. product-form reads FormData synchronously on
    // submit, so the main form is restored right after the click.
    const existingInput = formElement.querySelector('input[name="quantity"]');
    const quantity = String(this.#quantity);
    /** @type {HTMLInputElement | null} */
    let temporaryInput = null;
    let previousValue = '';

    if (existingInput instanceof HTMLInputElement && !existingInput.disabled) {
      previousValue = existingInput.value;
      existingInput.value = quantity;
    } else {
      temporaryInput = document.createElement('input');
      temporaryInput.type = 'hidden';
      temporaryInput.name = 'quantity';
      temporaryInput.value = quantity;
      existingInput?.removeAttribute('name');
      formElement.append(temporaryInput);
    }

    // Skip the main button's own animations; it is usually off screen.
    mainButton.dataset.puppet = 'true';
    try {
      mainButton.click();
    } finally {
      mainButton.dataset.puppet = 'false';
      if (temporaryInput) {
        temporaryInput.remove();
        existingInput?.setAttribute('name', 'quantity');
      } else if (existingInput instanceof HTMLInputElement) {
        existingInput.value = previousValue;
      }
    }
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #productForm() {
    return (
      this.#section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`) ?? null
    );
  }

  get #sizeFieldset() {
    const { sizeIndex, productId } = this.dataset;
    if (sizeIndex === undefined) return null;

    return (
      this.#section?.querySelector(
        `variant-picker[data-product-id="${productId}"] fieldset[data-fieldset-index="${sizeIndex}"]`
      ) ?? null
    );
  }

  get #isPanelOpen() {
    const { sizePanel } = this.refs;
    return Boolean(sizePanel && !sizePanel.hidden);
  }

  #openSizePanel() {
    const { sizePanel, sizeTrigger, sizeOptions = [] } = this.refs;
    if (!sizePanel || !sizeTrigger) return;

    sizePanel.hidden = false;
    sizeTrigger.setAttribute('aria-expanded', 'true');

    const focusTarget =
      sizeOptions.find((option) => option.getAttribute('aria-pressed') === 'true') ??
      sizeOptions.find((option) => !option.disabled);
    focusTarget?.focus();
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  #closeSizePanel({ restoreFocus = false } = {}) {
    const { sizePanel, sizeTrigger } = this.refs;
    if (!sizePanel || !sizeTrigger || sizePanel.hidden) return;

    sizePanel.hidden = true;
    sizeTrigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) sizeTrigger.focus();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.#isPanelOpen) {
      event.stopPropagation();
      this.#closeSizePanel({ restoreFocus: true });
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handleOutsidePointer = (event) => {
    if (!this.#isPanelOpen || !(event.target instanceof Node)) return;
    if (!this.refs.bar.contains(event.target)) this.#closeSizePanel();
  };

  /**
   * @param {string} value
   * @returns {HTMLInputElement | undefined}
   */
  #findMainSizeRadio(value) {
    const radios = this.#sizeFieldset?.querySelectorAll('input[type="radio"]') ?? [];

    for (const radio of radios) {
      if (radio instanceof HTMLInputElement && radio.value === value) return radio;
    }

    return undefined;
  }

  /**
   * Keeps the sticky size in line with sizes picked in the main variant picker.
   * @param {Event} event
   */
  #handleMainChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement) || this.contains(target)) return;
    if (!this.#sizeFieldset?.contains(target)) return;

    this.#selectedSize = target.value;
    this.#render();
  };

  #syncFromMain() {
    const fieldset = this.#sizeFieldset;
    const picker = fieldset?.closest('variant-picker');
    const checked = fieldset?.querySelector('input:checked');

    if (checked instanceof HTMLInputElement && !picker?.hasAttribute('data-size-pending')) {
      this.#selectedSize = checked.value;
    }

    this.#render();
  }

  #render() {
    const { sizeTrigger, sizeValue, sizeOptions = [], addButton, addLabel, addPrice } = this.refs;
    if (!sizeTrigger) return;

    const { selectSizeText = '', addToCartText = '', soldOutText = '' } = this.dataset;
    const selected = sizeOptions.find((option) => option.value === this.#selectedSize);
    const available = selected?.dataset.available === 'true';

    for (const option of sizeOptions) {
      option.setAttribute('aria-pressed', String(option === selected));
    }

    if (sizeValue) sizeValue.textContent = selected ? selected.value : selectSizeText;

    addButton.setAttribute('aria-disabled', String(!available));
    addLabel.textContent = !selected ? selectSizeText : available ? addToCartText : soldOutText;
    addPrice.hidden = !available;

    if (available && selected?.dataset.price) addPrice.textContent = selected.dataset.price;
  }

  /**
   * @param {number} quantity
   */
  #setQuantity(quantity) {
    this.#quantity = Math.max(1, quantity);
    const { quantityValue } = this.refs;
    if (quantityValue) quantityValue.textContent = String(this.#quantity);
  }

  #promptSizeSelection() {
    const { sizeTrigger } = this.refs;
    if (!sizeTrigger) return;

    this.#openSizePanel();

    sizeTrigger.classList.remove('katua-sticky__size-trigger--attention');
    void sizeTrigger.offsetWidth;
    sizeTrigger.classList.add('katua-sticky__size-trigger--attention');
    sizeTrigger.addEventListener(
      'animationend',
      () => sizeTrigger.classList.remove('katua-sticky__size-trigger--attention'),
      { once: true }
    );
  }

  #observe() {
    const productForm = this.#productForm;
    const buyButtons = productForm?.closest('.buy-buttons-block') ?? productForm;
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (!buyButtons) {
      this.#buyButtonsVisible = false;
      this.#updateVisibility();
    }

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === buyButtons) {
          // Show only after the buy buttons scroll above the viewport, not before reaching them.
          this.#buyButtonsVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
        } else {
          this.#footerVisible = entry.isIntersecting;
        }
      }

      this.#updateVisibility();
    });

    if (buyButtons) this.#observer.observe(buyButtons);
    if (footer) this.#observer.observe(footer);
  }

  #updateVisibility() {
    const hiddenOnDesktop = this.classList.contains('katua-sticky--mobile-only') && this.#desktopQuery.matches;
    const visible = !this.#buyButtonsVisible && !this.#footerVisible && !hiddenOnDesktop;
    const { bar } = this.refs;

    if (!visible) this.#closeSizePanel();

    if (typeof bar.showPopover === 'function') {
      const open = bar.matches(':popover-open');
      if (visible && !open) bar.showPopover();
      if (!visible && open) bar.hidePopover();
    } else {
      bar.dataset.visible = String(visible);
    }
  }
}

if (!customElements.get('katua-sticky-atc')) {
  customElements.define('katua-sticky-atc', KatuaStickyAtc);
}
